import {
  BackwardsCompatibleLocationInformationFragment,
  EntityDetailsFragment,
  EntityFieldFragment,
  EntityLayerType,
} from 'types/graphql'

/** Runtime values for the GraphQL EntityLayerType union. */
export const EntityLayerTypes = {
  Address: 'Address',
  Other: 'Other',
  Parcel: 'Parcel',
} as const satisfies Record<string, EntityLayerType>

type CountableEntityLayer = {
  type?: EntityLayerType | null
}

type EntityLayerIconKey = 'FaHouse' | 'FaMap' | 'FaSquareDashed'

/** GIS layers on the org — used for multi-layer display behavior (title prefixes, field sort). */
export const getCountableEntityLayers = (
  entityLayers: CountableEntityLayer[] | null | undefined
): CountableEntityLayer[] => entityLayers ?? []

/** Whether the org has more than one staff-facing GIS layer (Address, Parcel, or Other). */
export const getOrganizationHasMultipleEntityLayers = (
  entityLayers: CountableEntityLayer[] | null | undefined
): boolean => getCountableEntityLayers(entityLayers).length > 1

/** Combined address/parcel labels only apply when the org has exactly one GIS layer. */
export const shouldCombineAddressAndParcelLabel = (
  entityLayerCount: number | null | undefined
): boolean => entityLayerCount === 1

type UniversalSearchEntityFragment = {
  name: string
  addressDisplayName?: string | null
  layer?: { type?: EntityLayerType | null; name?: string | null } | null
}

/** Subtitle line for universal search entity hits (Address / Parcel / Other). */
export const getUniversalSearchEntitySubtitle = (
  entity: UniversalSearchEntityFragment,
  layerType: EntityLayerType
): string => {
  if (layerType === EntityLayerTypes.Other) {
    return entity.layer?.name ?? entity.name
  }

  const isParcel = layerType === EntityLayerTypes.Parcel
  if (isParcel && entity.addressDisplayName) {
    return entity.addressDisplayName
  }

  return entity.name
}

type EntityLayerSwitcherFragment = {
  type: EntityLayerType
  name: string
}

type GetShowPinForEntityMapShapeArgs = {
  entityLayerType: EntityLayerType
  coordinateCount: number
}

/** Whether a map shape should render a centroid pin (polygon fills remain clickable for selection). */
export const getShowPinForEntityMapShape = (_args: GetShowPinForEntityMapShapeArgs): boolean => true

/** Label for the EV map layer switcher — Other layers use their configured name. */
export const getEntityLayerMapSwitcherLabel = (
  entityLayer: EntityLayerSwitcherFragment
): string => {
  if (entityLayer.type === EntityLayerTypes.Other) {
    return entityLayer.name
  }

  return entityLayer.type
}

const ENTITY_LAYER_MAP_SWITCHER_TYPE_ORDER: Record<EntityLayerType, number> = {
  [EntityLayerTypes.Address]: 0,
  [EntityLayerTypes.Parcel]: 1,
  [EntityLayerTypes.Other]: 2,
}

/** Stable sort for map/search layer switchers: Address, then Parcel, then Other by name. */
export const sortEntityLayersForMapSwitcher = <Layer extends EntityLayerSwitcherFragment>(
  entityLayers: Layer[]
): Layer[] =>
  [...entityLayers].sort((layerA, layerB) => {
    const typeOrderDiff =
      ENTITY_LAYER_MAP_SWITCHER_TYPE_ORDER[layerA.type] -
      ENTITY_LAYER_MAP_SWITCHER_TYPE_ORDER[layerB.type]
    if (typeOrderDiff !== 0) {
      return typeOrderDiff
    }

    return layerA.name.localeCompare(layerB.name)
  })

/** Icon key for entity version search results and similar layer-type affordances. */
export const getEntityLayerTypeIconKey = (
  layerType: EntityLayerType | undefined | null
): EntityLayerIconKey => {
  if (layerType === EntityLayerTypes.Address) {
    return 'FaHouse'
  }
  if (layerType === EntityLayerTypes.Other) {
    return 'FaMap'
  }
  return 'FaSquareDashed'
}

/**
 * Page title for entity detail views. Other layers use the configured layer name
 * instead of the generic "Address" / "Parcel" prefixes.
 */
export const getEntityPageTitle = (
  entity: EntityDetailsFragment,
  layerType: EntityLayerType,
  organizationHasMultipleEntityLayers: boolean
): string => {
  if (layerType === EntityLayerTypes.Other) {
    const layerName = entity.layer?.name ?? 'Entity'
    if (organizationHasMultipleEntityLayers) {
      return `${layerName} ${entity.name ?? ''}`.trim()
    }
    return entity.name ?? ''
  }

  if (organizationHasMultipleEntityLayers) {
    return `${layerType === EntityLayerTypes.Address ? 'Address' : 'Parcel'} ${entity.name ?? ''}`.trim()
  }

  const addressField = entity.latestVersion?.fields?.find(
    (field) => field.entityFieldTemplate?.isAddressDisplayName
  )
  const parcelField = entity.latestVersion?.fields?.find(
    (field) => field.entityFieldTemplate?.isParcelDisplayName
  )
  if (addressField && parcelField) {
    return `${addressField.stringifiedValue ?? ''} (Parcel: ${parcelField.stringifiedValue ?? ''})`
  }
  if (addressField) {
    return addressField.stringifiedValue ?? ''
  }
  if (parcelField) {
    return parcelField.stringifiedValue ?? ''
  }
  return ''
}

type SortEntityVersionFieldsArgs = {
  fields: EntityFieldFragment[]
  entityLayerType: EntityLayerType
  organizationHasMultipleEntityLayers: boolean
  entityName?: string | null
}

const getAddressParcelFieldSortPriority = (field: EntityFieldFragment): number => {
  if (field.entityFieldTemplate?.isAddressDisplayName) {
    return 0
  }
  if (field.entityFieldTemplate?.isParcelDisplayName) {
    return 1
  }
  return 2
}

const getOtherLayerFieldSortPriority = (
  field: EntityFieldFragment,
  entityName?: string | null
): number => {
  const normalizedEntityName = entityName?.trim().toLowerCase()
  const fieldValue = field.stringifiedValue?.trim().toLowerCase()

  if (normalizedEntityName && fieldValue === normalizedEntityName) {
    return 0
  }

  if (field.entityFieldTemplate?.label?.trim().toLowerCase() === 'name') {
    return 1
  }

  return 2
}

/**
 * Sort order for entity version field tables. Address/Parcel display-name fields
 * float to the top for legacy layers; Other layers prioritize name-like fields.
 */
export const sortEntityVersionFields = ({
  fields,
  entityLayerType,
  organizationHasMultipleEntityLayers,
  entityName,
}: SortEntityVersionFieldsArgs): EntityFieldFragment[] => {
  return [...fields].sort((fieldA, fieldB) => {
    if (!organizationHasMultipleEntityLayers) {
      if (entityLayerType === EntityLayerTypes.Other) {
        const priorityDiff =
          getOtherLayerFieldSortPriority(fieldA, entityName) -
          getOtherLayerFieldSortPriority(fieldB, entityName)
        if (priorityDiff !== 0) {
          return priorityDiff
        }
      } else {
        const priorityDiff =
          getAddressParcelFieldSortPriority(fieldA) - getAddressParcelFieldSortPriority(fieldB)
        if (priorityDiff !== 0) {
          return priorityDiff
        }
      }
    }

    const labelA = fieldA.entityFieldTemplate?.label || ''
    const labelB = fieldB.entityFieldTemplate?.label || ''
    return labelA.localeCompare(labelB)
  })
}

type GetRecordAddressLabelArgs = {
  address?: string | null
  parcel?: string | null
  entityLayerCount?: number | null
  entityName?: string | null
  layerName?: string | null
}

/**
 * Fallback label from legacy location field rows when address/parcel strings are absent.
 * Used until BackwardsCompatibleLocationInformation exposes Other-entity identifiers directly.
 */
export const getLocationFieldFallbackLabel = (
  fields: BackwardsCompatibleLocationInformationFragment['fields']
): string | null => {
  const firstPopulatedField = fields?.find((field) => field.value?.trim())
  return firstPopulatedField?.value?.trim() ?? null
}

/** Plain-text location label for record tables and titles (no link wrapper). */
export const getRecordAddressLabel = ({
  address,
  parcel,
  entityLayerCount,
  entityName,
  layerName,
}: GetRecordAddressLabelArgs): string | null => {
  if (address && parcel && shouldCombineAddressAndParcelLabel(entityLayerCount)) {
    return `${address} (Parcel: ${parcel})`
  }
  if (address) {
    return address
  }
  if (parcel) {
    return parcel
  }
  if (entityName) {
    return entityName
  }
  if (layerName) {
    return layerName
  }
  return null
}
